import { element, replaceChildren } from './dom.js';

function initials(user) {
  const source = String(user?.displayName || user?.email || '?').trim();
  const parts = source.split(/\s+|@/).filter(Boolean);
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
}

export function renderInitializing(root, message = 'Preparazione di A11yPilot…') {
  const heading = element('h1', { className: 'boot-title' }, 'A11yPilot');
  replaceChildren(root, element('main', { className: 'boot-state', id: 'main-content' }, [
    element('img', {
      className: 'boot-brand',
      src: '/assets/a11ypilot-mark.svg',
      alt: '',
      width: '200',
      height: '200',
      'aria-hidden': 'true',
    }),
    element('span', { className: 'boot-spinner', 'aria-hidden': 'true' }),
    heading,
    element('p', { role: 'status' }, message),
  ]));
  root.setAttribute('aria-busy', 'true');
}

export function renderFatalState(root, { title, body, detail = '' }) {
  const heading = element('h1', { className: 'system-state__title', tabindex: '-1' }, title);
  replaceChildren(root, element('main', { className: 'system-page', id: 'main-content' }, [
    element('section', { className: 'system-state', role: 'alert' }, [
      element('span', { className: 'material-icons system-state__icon', 'aria-hidden': 'true' }, 'error_outline'),
      heading,
      element('p', { className: 'system-state__body' }, body),
      detail ? element('p', { className: 'system-state__detail' }, detail) : null,
    ]),
  ]));
  root.setAttribute('aria-busy', 'false');
  queueMicrotask(() => heading.focus());
}

export function createAuthenticatedShell(root, { user, tenantId, onSignOut, onToggleUsers = null }) {
  const main = element('main', { className: 'app-main', id: 'main-content', tabindex: '-1' });
  /* Contenitore per la navigazione di primo livello. Al centro della barra
     stava «Workspace justbit Modifica»: né identità né navigazione, e per
     colpa sua i tab dell'applicazione finivano in una fascia vuota tutta per
     loro, sotto, orfani. Il posto al centro ce l'ha la navigazione. */
  const nav = element('div', { className: 'shell-nav' });
  const accessBadge = element('span', { className: 'shell-access', hidden: true });
  /* La gestione degli utenti non è un tab dell'applicazione: la barra centrale
     appartiene alla navigazione degli audit e la vista se la riscrive a ogni
     render. Sta accanto all'account, dov'è l'identità, e compare solo a chi
     può usarla. */
  const usersButton = element('button', {
    className: 'shell-users',
    type: 'button',
    hidden: true,
    'aria-pressed': 'false',
    onClick: () => onToggleUsers?.(),
  }, [
    element('span', { className: 'material-icons', 'aria-hidden': 'true' }, 'group'),
    element('span', { className: 'shell-users__label' }, 'Utenti'),
  ]);
  const signOutButton = element('button', {
    className: 'shell-signout',
    type: 'button',
    onClick: async () => {
      signOutButton.disabled = true;
      signOutButton.setAttribute('aria-busy', 'true');
      try {
        await onSignOut();
      } finally {
        if (signOutButton.isConnected) {
          signOutButton.disabled = false;
          signOutButton.removeAttribute('aria-busy');
        }
      }
    },
  }, [
    element('span', { className: 'material-icons', 'aria-hidden': 'true' }, 'logout'),
    element('span', { className: 'shell-signout__label' }, 'Esci'),
  ]);

  const shell = element('div', { className: 'app-shell', 'data-ui': 'v2' }, [
    element('header', { className: 'shell-header' }, [
      element('a', { className: 'shell-brand', href: '/', 'aria-label': 'A11yPilot, pagina iniziale' }, [
        element('span', { className: 'shell-brand__icon', 'aria-hidden': 'true' },
          element('img', {
            className: 'shell-brand__mark',
            src: '/assets/a11ypilot-mark.svg',
            alt: '',
            width: '200',
            height: '200',
          })),
        element('span', { className: 'shell-brand__name' }, 'A11yPilot'),
      ]),
      nav,
      element('div', { className: 'shell-account' }, [
        accessBadge,
        usersButton,
        element('span', { className: 'shell-avatar', 'aria-hidden': 'true' }, initials(user)),
        element('span', { className: 'shell-account__copy' }, [
          element('strong', {}, user?.displayName || 'Account'),
          element('span', {}, user?.email || ''),
        ]),
        signOutButton,
      ]),
    ]),
    main,
  ]);

  replaceChildren(root, shell);
  root.setAttribute('aria-busy', 'false');

  /* Il permesso si segnala solo quando LIMITA. «Modifica» su ogni schermata
     era rumore: è il caso normale. «Sola lettura» invece va detto, altrimenti
     chi non può scrivere scopre il limite premendo un pulsante spento. */
  function setAccess(canManage) {
    accessBadge.hidden = canManage;
    accessBadge.className = 'shell-access shell-access--readonly';
    accessBadge.textContent = canManage ? '' : 'Sola lettura';
  }

  /** Il pannello Utenti è per owner e admin: agli altri non si mostra. */
  function setUsersAvailable(available) {
    usersButton.hidden = !available;
  }

  function setUsersActive(active) {
    usersButton.setAttribute('aria-pressed', active ? 'true' : 'false');
    usersButton.classList.toggle('is-active', Boolean(active));
    usersButton.querySelector('.shell-users__label').textContent = active ? 'Torna agli audit' : 'Utenti';
  }

  return Object.freeze({
    main,
    nav,
    tenantId,
    setAccess,
    setUsersActive,
    setUsersAvailable,
    destroy: () => shell.remove(),
  });
}

export function renderShellState(root, {
  icon = 'hourglass_top',
  title,
  body,
  tone = 'neutral',
  actions = [],
  busy = false,
}) {
  const heading = element('h1', { className: 'shell-state__title', tabindex: '-1' }, title);
  const actionRow = actions.length
    ? element('div', { className: 'shell-state__actions' }, actions.map((action) => element('button', {
        className: action.primary ? 'v2-btn v2-btn-primary' : 'v2-btn v2-btn-secondary',
        type: 'button',
        onClick: action.onClick,
      }, action.label)))
    : null;

  replaceChildren(root, element('section', {
    className: `shell-state shell-state--${tone}`,
    'aria-busy': String(Boolean(busy)),
  }, [
    busy ? element('span', { className: 'boot-spinner', 'aria-hidden': 'true' })
      : element('span', { className: 'material-icons shell-state__icon', 'aria-hidden': 'true' }, icon),
    heading,
    element('p', { className: 'shell-state__body' }, body),
    actionRow,
  ]));
  if (!busy) queueMicrotask(() => heading.focus());
}
